// src/components/Ownerdashboard/OwnerStatusControl.jsx
// Active / Sold-Rent Out toggle shown on every owner property card. Nothing is
// sent until the owner confirms - closing a listing takes it off the search.
import React, { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { getOwnerId, updateOwnerPropertyStatus } from "./ownerAuth";

const OwnerStatusControl = ({ property, onUpdated, className = "" }) => {
  const storedStatus = property?.propertyCurrentStatus || "";
  const [status, setStatus] = useState(storedStatus);
  const [pendingStatus, setPendingStatus] = useState("");
  const [saving, setSaving] = useState(false);

  // Rent listings close as "Rent out", everything else closes as "Sell out".
  const isRentType = String(property?.type || "").toLowerCase().includes("rent");
  const closedValue = isRentType ? "Rent out" : "Sell out";
  const closedLabel = isRentType ? "Rent Out" : "Sold Out";

  // Anything that is not the closed status counts as live.
  const isClosed = status.trim().toLowerCase() === closedValue.toLowerCase();
  const pendingIsClosed = pendingStatus === closedValue;

  // A reload hands back a fresh property object; follow whatever it now holds.
  useEffect(() => {
    setStatus(storedStatus);
  }, [storedStatus]);

  const requestChange = (nextStatus) => {
    const nextIsClosed = nextStatus === closedValue;
    if (saving || nextIsClosed === isClosed) return;
    setPendingStatus(nextStatus);
  };

  const handleConfirm = async () => {
    setSaving(true);

    // Only the status transition travels - the listing itself is untouched.
    const result = await updateOwnerPropertyStatus({
      propId: property?._id,
      // `userId` is the owner making the change - the id verify-otp handed back.
      userId: getOwnerId() || "",
      newStatus: pendingStatus,
      previousStatus: status || "Active",
      statusField: "status",
    });

    setSaving(false);

    if (result.success) {
      setStatus(pendingStatus);
      setPendingStatus("");
      toast.success(result.message || "Status updated successfully.");
      onUpdated?.(result);
      return;
    }

    setPendingStatus("");
    toast.error(result.message || "Failed to update the status.");
    if (result.unauthorized) onUpdated?.(result);
  };

  const segment = (selected, activeStyle) =>
    `rounded-full px-3 py-1 text-xs font-semibold transition-all duration-200 xl:px-4 xl:py-1.5 xl:text-sm ${
      selected ? `${activeStyle} shadow-sm` : "text-gray-500 hover:text-gray-800"
    }`;

  return (
    <>
      <div
        role="group"
        aria-label="Property status"
        onClick={(e) => e.stopPropagation()}
        className={`inline-flex items-center gap-0.5 rounded-full border border-gray-200 bg-gray-100 p-0.5 ${className}`}
      >
        <button
          type="button"
          onClick={() => requestChange("Active")}
          aria-pressed={!isClosed}
          className={segment(!isClosed, "bg-emerald-500 text-white")}
        >
          Active
        </button>
        <button
          type="button"
          onClick={() => requestChange(closedValue)}
          aria-pressed={isClosed}
          className={segment(isClosed, "bg-red-500 text-white")}
        >
          {closedLabel}
        </button>
      </div>

      {pendingStatus && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          onClick={() => !saving && setPendingStatus("")}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <span
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                  pendingIsClosed ? "bg-red-50 text-red-500" : "bg-emerald-50 text-emerald-500"
                }`}
              >
                {pendingIsClosed ? (
                  <AlertTriangle className="h-5 w-5" />
                ) : (
                  <CheckCircle2 className="h-5 w-5" />
                )}
              </span>

              <div>
                <h3 className="text-base font-bold text-gray-800">
                  {pendingIsClosed ? `Mark as ${closedLabel}?` : "Set back to Active?"}
                </h3>
                <p className="mt-1.5 text-sm text-gray-600">
                  <span className="font-semibold text-[#503691]">
                    {property?.title || "This property"}
                  </span>{" "}
                  {pendingIsClosed
                    ? "will be hidden from brokers searching on PRObroker."
                    : "will be visible to brokers on PRObroker again."}
                </p>
                <p className="mt-1 text-xs text-gray-400">ID: {property?.propertyId || "NA"}</p>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setPendingStatus("")}
                disabled={saving}
                className="rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={saving}
                className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 ${
                  pendingIsClosed
                    ? "bg-red-500 hover:bg-red-600"
                    : "bg-emerald-500 hover:bg-emerald-600"
                }`}
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {saving
                  ? "Updating..."
                  : pendingIsClosed
                    ? `Yes, ${closedLabel}`
                    : "Yes, Activate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OwnerStatusControl;
